import { CONTACT } from "@/data/contact";

export type PlanId = "free" | "standard" | "custom";

/** Formats rupees the Indian way, e.g. 14999 -> "₹14,999". */
export const inr = (amount: number) => `₹${amount.toLocaleString("en-IN")}`;

// The single source for plan prices: the cards, comparison table, page
// description and JSON-LD all read these.
const STANDARD_MIN = 14999;
const STANDARD_MAX = 24999;
export const STANDARD_RANGE = `${inr(STANDARD_MIN)}–${inr(STANDARD_MAX)}`;

export interface Plan {
  id: PlanId;
  name: string;
  /** Full service name, used for the schema.org Offer. */
  fullName: string;
  /** Price in rupees (min === max for a fixed price); absent when quoted by scope. */
  priceRange?: { min: number; max: number };
  /** Headline price shown on the card. */
  price: string;
  priceNote: string;
  tagline: string;
  /** Short list for the plan card; the full list is in `included`. */
  highlights: string[];
  heading: string;
  intro: string;
  included: string[];
  /** Plan-specific terms, shown under the full list. */
  terms: { label: string; text: string }[];
  footnote: string;
  /** Starting point for the enquiry message when this plan is selected. */
  draft: string;
}

export const DEFAULT_PLAN: PlanId = "standard";

export const PLANS: Plan[] = [
  {
    id: "free",
    name: "Free",
    fullName: "Free Business Website",
    priceRange: { min: 0, max: 0 },
    price: inr(0),
    priceNote: "For eligible businesses",
    tagline: "A basic online presence at no website development cost.",
    highlights: [
      "4 pages: Home, About, Services, Contact",
      "Mobile-friendly, predefined layout",
      "Hosting on yourcompany.hbtintegrisys.com for 1 year",
      "Google Business Profile setup assistance",
    ],
    heading: `Free Business Website — ${inr(0)}`,
    intro:
      "Get your business online at no website development cost. HBT Integrisys helps eligible small businesses establish a basic online presence with a simple, mobile-friendly website.",
    included: [
      "4 pages: Home, About, Services and Contact",
      "Responsive design for mobile, tablet and desktop",
      "Free hosting under a yourcompany.hbtintegrisys.com subdomain for 1 year",
      "Business information and contact details",
      "Basic website setup, testing and launch",
      "Assistance with setting up your Google Business Profile, subject to eligibility and Google's verification process",
    ],
    terms: [
      { label: "Eligibility", text: "Available exclusively to eligible businesses." },
      {
        label: "Limitations",
        text: "This offer uses a predefined layout and does not include a custom domain, advanced integrations, contact forms, WhatsApp buttons, extensive content creation or ongoing maintenance.",
      },
      {
        label: "After the first year",
        text: "Continued hosting and subdomain availability are subject to HBT Integrisys' renewal policy. Custom domains, additional functionality and services beyond this scope may be purchased separately.",
      },
    ],
    footnote:
      "HBT Integrisys reserves the right to review applications and define the delivery schedule based on availability.",
    draft:
      "Hi HBT Integrisys,\n\nI'd like to apply for the Free Business Website plan (Home, About, Services and Contact pages, hosted on a hbtintegrisys.com subdomain for 1 year, with help setting up our Google Business Profile).\n\nBusiness name:\nWhat we do:\nLocation:\n\nPlease let me know if we're eligible and what you need from us.",
  },
  {
    id: "standard",
    name: "Standard",
    fullName: "Standard Website Development",
    priceRange: { min: STANDARD_MIN, max: STANDARD_MAX },
    price: inr(STANDARD_MIN),
    priceNote: `Starting price · typical range ${STANDARD_RANGE}`,
    tagline: "A professional website personalised to your brand.",
    highlights: [
      "5–7 professionally designed pages",
      "Your branding, first-year domain and hosting",
      "WhatsApp, Google Maps and contact form",
      "Basic SEO and Google Business Profile setup",
      "3 months of limited post-launch support",
    ],
    heading: "Standard Website Development",
    intro:
      `Starting from ${inr(STANDARD_MIN)}, with a typical range of ${STANDARD_RANGE}. Get a professional, mobile-responsive website tailored to your business needs.`,
    included: [
      "5–7 professionally designed website pages",
      "Business-specific branding and responsive layouts",
      "First-year domain and hosting",
      "WhatsApp integration, Google Maps and contact form",
      "Photo gallery and basic SEO setup",
      "Google Business Profile setup assistance",
      "Content organization and basic copy assistance",
      "Three months of limited post-launch support",
    ],
    terms: [
      {
        label: "Pricing depends on",
        text: "The design complexity, number of pages, content requirements, integrations and overall development effort.",
      },
      {
        label: "Additional charges",
        text: "Additional pages, advanced functionality, extensive content creation, premium third-party services and requests outside the agreed scope may incur additional charges. All additional costs will be communicated and approved before work begins.",
      },
    ],
    footnote:
      "The final quotation will be provided after understanding your requirements. Domain and hosting renewals after the first year are charged separately.",
    draft:
      "Hi HBT Integrisys,\n\nI'm interested in the Standard Website Development plan (5–7 pages with our branding, domain and hosting for the first year, WhatsApp, Google Maps, contact form and basic SEO).\n\nBusiness name:\nPages we need:\nWebsites we like:\n\nPlease share a quotation for our requirement.",
  },
  {
    id: "custom",
    name: "Custom",
    fullName: "Custom Website Development",
    price: "Tailored quote",
    priceNote: "Quoted individually by scope",
    tagline: "For requirements that go beyond a standard website.",
    highlights: [
      "Requirements analysis and technical planning",
      "Custom UI/UX, features and business logic",
      "Backend, database and APIs where required",
      "Integrations, SEO and analytics as agreed",
      "Training and support as defined in the proposal",
    ],
    heading: "Custom Website Development — Tailored Pricing",
    intro:
      "Your business requirements. Your solution. Built to scale. We develop customized websites and web applications designed around your business processes, customer experience and growth objectives.",
    included: [
      "Business requirements analysis and technical planning",
      "Custom UI/UX design and responsive development",
      "Flexible pages, layouts and website architecture",
      "Custom features, workflows and business logic",
      "Backend, database and API development where required",
      "Third-party integrations based on project requirements",
      "Agreed SEO, analytics and performance optimization",
      "Testing, deployment and project handover",
      "Training and post-launch support as defined in the proposal",
    ],
    terms: [
      {
        label: "Suitable for",
        text: "E-commerce platforms, booking systems, customer portals, dashboards, business automation and AI-enabled web applications.",
      },
      {
        label: "Pricing",
        text: "Every project is different. We provide a tailored quotation based on functionality, complexity, integrations, delivery timeline and support requirements.",
      },
      {
        label: "How it works",
        text: "We discuss your requirements, define the scope, prepare a proposal and timeline, and begin development after approval.",
      },
      {
        label: "Additional services",
        text: "Hosting, domain renewals, third-party subscriptions, ongoing maintenance and new features are quoted separately unless explicitly included in your proposal.",
      },
    ],
    footnote: "Contact HBT Integrisys to discuss your requirements and receive a project estimate.",
    draft:
      "Hi HBT Integrisys,\n\nWe have a requirement that needs a custom solution.\n\nBusiness name:\nWhat we want to build (e.g. booking system, customer portal, e-commerce, dashboard):\nKey features:\nIntegrations needed:\nPreferred timeline:\n\nPlease get in touch to discuss the scope and an estimate.",
  },
];

export const PLAN_BY_ID = Object.fromEntries(PLANS.map((p) => [p.id, p])) as Record<PlanId, Plan>;

/** Rows of the comparison table, one cell per plan. */
export const COMPARISON: { feature: string; values: Record<PlanId, string> }[] = [
  {
    feature: "Purpose",
    values: { free: "Basic online presence", standard: "Professional business website", custom: "Business-specific digital solution" },
  },
  {
    feature: "Design",
    values: { free: "Predefined layout", standard: "Customized from a reusable codebase", custom: "Tailored UI/UX and architecture" },
  },
  {
    feature: "Personalization",
    values: {
      free: "Your business details in a fixed layout",
      standard: "Your branding, colours and photos",
      custom: "Built around your processes and customers",
    },
  },
  {
    feature: "Pages",
    values: { free: "4 fixed pages", standard: "5–7 pages", custom: "Defined by requirements" },
  },
  {
    feature: "Domain & hosting",
    values: {
      free: "hbtintegrisys.com subdomain for 1 year",
      standard: "Your own domain and hosting for the first year",
      custom: "Quoted separately unless in proposal",
    },
  },
  {
    feature: "SEO",
    values: {
      free: "Google Business Profile assistance",
      standard: "Basic SEO setup + Google Business Profile",
      custom: "Agreed SEO, analytics and performance optimization",
    },
  },
  {
    feature: "Backend/database",
    values: { free: "Not included", standard: "Not included by default", custom: "Available where needed" },
  },
  {
    feature: "Advanced integrations",
    values: { free: "Not included", standard: "Limited standard features", custom: "Quoted by scope" },
  },
  {
    feature: "Pricing",
    values: { free: inr(0), standard: `${STANDARD_RANGE} proposed range`, custom: "Individual quotation" },
  },
  {
    feature: "Support",
    values: { free: "Defined basic scope", standard: "3 months, with limits", custom: "As agreed in proposal" },
  },
  {
    feature: "Support hours",
    values: { free: CONTACT.hours, standard: CONTACT.hours, custom: CONTACT.hours },
  },
];

/** Terms that apply to every plan. */
export const GENERAL_TERMS = [
  "All prices are in Indian Rupees (₹). The Standard range is indicative; your final price is confirmed in a written quotation before work begins.",
  "The scope of every project — pages, features, integrations and support — is agreed in writing before development starts. Work outside the agreed scope is quoted separately and only begins after your approval.",
  "You provide your business information, logo, photos and other content, and confirm you have the right to use them. Extensive content creation is not included unless stated in your plan or proposal.",
  "Domains, hosting renewals, premium plugins and other third-party services are subject to their providers' pricing and terms, and are charged separately after any period included in your plan.",
  "Google Business Profile setup is assistance only. Listing approval and verification are controlled by Google, and HBT Integrisys cannot guarantee them or any search ranking.",
  `Support is provided during business hours (${CONTACT.hours}) within the support period and limits of your plan or proposal.`,
  "HBT Integrisys may review, accept or decline applications for the Free plan, and schedules delivery based on availability.",
];

export const PRICING_FAQS = [
  {
    q: "When should I choose Free, Standard or Custom?",
    a: "Choose Free if you're a business that needs a simple online presence — who you are, what you offer and how to reach you — and you're happy with a predefined layout on a hbtintegrisys.com subdomain. Choose Standard if you want a professional website with your own domain, your branding, 5–7 pages, WhatsApp, Google Maps, a contact form and basic SEO. Choose Custom when your requirement can't reasonably fit a standard website: online bookings, payments, customer logins, dashboards, workflows, a database or integrations with other systems.",
  },
  {
    q: "Who is eligible for the Free plan?",
    a: "The Free plan is only for businesses. We review each application and confirm eligibility before we start, and we schedule delivery based on our availability.",
  },
  {
    q: "What happens after the first year on the Free plan?",
    a: "Continued hosting and subdomain availability are subject to our renewal policy, which we'll share with you before the year ends. You can also move to the Standard plan for your own domain and more features.",
  },
  {
    q: "Can I use my own domain (yourcompany.com) on the Free plan?",
    a: "No. The Free plan is hosted on a yourcompany.hbtintegrisys.com subdomain. The Standard plan includes your own domain and hosting for the first year.",
  },
  {
    q: "Why is the Standard plan a price range instead of a fixed price?",
    a: "The final price depends on the design complexity, number of pages, the content you need help with and the integrations you choose. We confirm the exact price in a quotation after understanding your requirement, and any additional cost is approved by you before work begins.",
  },
  {
    q: "Why does Custom have no listed price?",
    a: "Custom is for requirements that a standard website can't reasonably cover, so every project is different. We discuss your requirement, define the scope and share a proposal with a timeline and quotation, so you only pay for what your business actually needs.",
  },
  {
    q: "Can I start with Free or Standard and upgrade later?",
    a: "Yes. Tell us what you want to add and we'll quote the upgrade based on the extra pages or features you need.",
  },
  {
    q: "What does support include, and when is it available?",
    a: `Support covers issues with the website we delivered and small adjustments within the plan's limits — three months for Standard, and as defined in the proposal for Custom. Our team is available during business hours, ${CONTACT.hours}. New pages, features and redesigns are quoted separately.`,
  },
  {
    q: "Will my website show up on Google?",
    a: "Every plan includes help setting up your Google Business Profile, Standard adds basic SEO setup, and Custom includes the SEO and analytics work agreed in your proposal. Listing verification is controlled by Google and no one can guarantee a specific ranking, but these steps give your business a strong start in local search.",
  },
  {
    q: "What do I need to provide?",
    a: "Your business details, logo, photos and the information you want on each page. For Standard we help organise your content and assist with basic copy; extensive content writing is quoted separately.",
  },
  {
    q: "How long does it take?",
    a: "Free websites are scheduled based on our availability. For Standard and Custom, the timeline is included in your quotation or proposal once the scope is agreed.",
  },
];
